import React, { useEffect, useRef, useState } from "react";
import Layout from "../../../components/template/Layout";
import ItemCard from "../../../components/template/Card/ItemCard";
import FilterDrawer from "../../../components/template/FilterDrawer/FilterDrawer";
import { FiSearch } from "react-icons/fi";
import { FaPlus, FaFilter, FaSpinner } from "react-icons/fa";
import PullToRefresh from "react-simple-pull-to-refresh";
import { useNavigate, useSearchParams } from "react-router-dom";
import { InboundItem } from "../../../types/item";
import ConfirmModal from "../../../components/template/ConfirmModal/ConfirmModal";
import {
  getInboundItems,
  deleteInboundItem,
} from "../../../redux/slices/InboundItemsSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import store from "../../../redux/store";
import { getInboundItemsFromDB } from "../../../db/schemas/inboundItems";
import {clearFormData} from "../../../redux/slices/inboundItemFormSlice";

const InboundItems: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [items, setItems] = useState<InboundItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<InboundItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [showDeleteItemModal, setShowDeleteItemModal] =
    useState<boolean>(false);
  const [showFilterDrawer, setShowFilterDrawer] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<InboundItem | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [packages, setPackages] = useState<
    { packageId: number; packageName: string }[]
  >([]);
  const [consignors, setConsignors] = useState<
    { consignorId: number; consignorName: string }[]
  >([]);
  const [locations, setLocations] = useState<
    { locationId: number; locationName: string }[]
  >([]);
  const myUserId = useSelector((state: RootState) => state.auth.userId);
   // Add this ref to track if we've already initialized
   const hasInitialized = useRef(false);

  // Helper function to get filters from URL
  const getFiltersFromURL = () => {
    return {
      status: searchParams.get("status") || "all",
      createdBy: searchParams.get("createdBy") || myUserId || "",
      locationId: searchParams.get("locationId") || "",
      consignorId: searchParams.get("consignorId") || "",
      packageId: searchParams.get("packageId") || "",
    };
  };

  // Helper function to update URL with current filters
  const updateURLWithFilters = (filters: typeof filter) => {
    const newSearchParams = new URLSearchParams();
    
    // Only add non-default values to URL
    if (filters.status !== "all") {
      newSearchParams.set("status", filters.status);
    }
    if (filters.createdBy !== myUserId && filters.createdBy !== "") {
      newSearchParams.set("createdBy", filters.createdBy);
    }
    if (filters.locationId !== "") {
      newSearchParams.set("locationId", filters.locationId);
    }
    if (filters.consignorId !== "") {
      newSearchParams.set("consignorId", filters.consignorId);
    }
    if (filters.packageId !== "") {
      newSearchParams.set("packageId", filters.packageId);
    }
    
    setSearchParams(newSearchParams, { replace: true });
  };

  const [filter, setFilter] = useState(getFiltersFromURL);

  useEffect(() => {
    dispatch(clearFormData());
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      fetchItems();
    }
  }, []);

  // Handle URL changes (browser back/forward navigation)
  useEffect(() => {
    const urlFilters = getFiltersFromURL();
    setFilter(urlFilters);
  }, [searchParams, myUserId]);

  useEffect(() => {
    const searchedItems = async () => {
      const searchedItems = await getFilteredItems(items);
      setFilteredItems(searchedItems);
    };
    searchedItems();
  }, [searchTerm, filter, items]);

  // Helper function to merge IndexedDB and server items
  const mergeItems = (indexDB: InboundItem[], server: InboundItem[]) => {
    const processedItems: InboundItem[] = [];
    const includedServerIds = new Set<number | string>();

    // Add all IndexedDB items (these may have pending changes or be new items)
    for (const indexDBItem of indexDB) {
      processedItems.push(indexDBItem);

      // Track which server items we've included
      if (indexDBItem.id && Number(indexDBItem.id) > 0) {
        includedServerIds.add(indexDBItem.id);
      }
    }

    // Add server items that aren't in IndexedDB
    for (const serverItem of server) {
      if (serverItem.id && !includedServerIds.has(serverItem.id)) {
        processedItems.push(serverItem);
      }
    }

    return processedItems;
  };

  const fetchItems = async () => {
    // ===== PHASE 0: Get IndexedDB items FIRST (pending/edited) =====
    const indexDBItems = (await getInboundItemsFromDB()) || [];
    console.log(" getting items from db");

    // ===== PHASE 1: Get Redux cached items and merge =====
    const cachedServerItems = (store.getState() as unknown as RootState)
      .inboundItems.items;
    console.log(" getting items from redux");
    // Merge IndexedDB (pending) with Redux (server) items
    const mergedItems = mergeItems(indexDBItems, cachedServerItems);
    console.log(" merging items");
    // Show merged items immediately (includes pending status)
    setItems(mergedItems);

    // ✅ SET LOADING TO FALSE HERE - Show cached items immediately
    setIsLoading(false);

    // Generate dropdowns from merged data
    const packages = await handleGeneratePackageList(mergedItems);
    setPackages(packages);

    const consignors = await handleGenerateConsignorList(mergedItems);
    setConsignors(consignors);

    const locations = await handleGenerateLocationList(mergedItems);
    setLocations(locations);

    // ===== PHASE 2: Fetch fresh data from server (background) =====
    try {
      setIsUpdating(true); // Use isUpdating for background fetch

      // Fetch fresh data from server
      await dispatch(getInboundItems()).unwrap();
      const freshServerItems = (store.getState() as unknown as RootState)
        .inboundItems.items;

      // Re-fetch IndexedDB in case it changed during API call
      const freshIndexDBItems = (await getInboundItemsFromDB()) || [];

      // Merge fresh data
      const freshItems = mergeItems(freshIndexDBItems, freshServerItems);

      // ✅ Update with fresh data
      setItems(freshItems);

      // Update dropdowns with fresh data
      const freshPackages = await handleGeneratePackageList(freshItems);
      setPackages(freshPackages);

      const freshConsignors = await handleGenerateConsignorList(freshItems);
      setConsignors(freshConsignors);

      const freshLocations = await handleGenerateLocationList(freshItems);
      setLocations(freshLocations);
    } catch (error) {
      console.error("Failed to fetch fresh items:", error);
      // Keep showing cached data - don't disrupt user experience
    } finally {
      // ✅ Only set isUpdating to false (not isLoading)
      setIsUpdating(false);
    }
  };

  const handleGeneratePackageList = async (items: InboundItem[]) => {
    const packageMap = new Map<
      number,
      { packageId: number; packageName: string }
    >();

    items.forEach((item) => {
      if (item.packageId) {
        if (packageMap.has(item.packageId)) {
        } else {
          packageMap.set(item.packageId, {
            packageId: item.packageId,
            packageName: item.package || "",
          });
        }
      }
    });

    return Array.from(packageMap.values()).sort(
      (a, b) => a.packageId - b.packageId
    );
  };

  const handleGenerateConsignorList = async (items: InboundItem[]) => {
    const consignorMap = new Map<
      number,
      { consignorId: number; consignorName: string }
    >();
    items.forEach((item) => {
      if (item.consignorId) {
        consignorMap.set(item.consignorId, {
          consignorId: item.consignorId,
          consignorName: item.consignor || "",
        });
      }
    });
    return Array.from(consignorMap.values()).sort(
      (a, b) => a.consignorId - b.consignorId
    );
  };

  const handleGenerateLocationList = async (items: InboundItem[]) => {
    const locationMap = new Map<
      number,
      { locationId: number; locationName: string }
    >();

    items.forEach((item) => {
      if (item.locationId) {
        if (locationMap.has(item.locationId)) {
        } else {
          locationMap.set(item.locationId, {
            locationId: item.locationId,
            locationName: item.location || "",
          });
        }
      }
    });
    return Array.from(locationMap.values()).sort(
      (a, b) => a.locationId - b.locationId
    );
  };

  // Apply search and filters
  const getFilteredItems = async (itemsToFilter: InboundItem[] = []) => {
    let filteredItems = [...itemsToFilter];

    // Apply search filter
    if (searchTerm) {
      filteredItems = filteredItems.filter(
        (item) =>
          item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          String(item.id).includes(searchTerm) ||
          item.consignorCode?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filter.createdBy == myUserId) {
      filteredItems = filteredItems.filter(
        (item) => item.createdBy_ID == myUserId
      );
    }

    if (filter.status !== "all") {
      filteredItems = filteredItems.filter((item) => {
        switch (filter.status) {
          case "pending":
            return item.isPending;
          case "completed":
            return !item.isPending;
          case "all":
            return true;
          default:
            return true;
        }
      });
    }

    if (filter.packageId !== "") {
      filteredItems = filteredItems.filter(
        (item) => item.packageId == Number(filter.packageId)
      );
    }

    if (filter.consignorId !== "") {
      filteredItems = filteredItems.filter(
        (item) => item.consignorId == Number(filter.consignorId)
      );
    }

    if (filter.locationId !== "") {
      filteredItems = filteredItems.filter(
        (item) => item.locationId == Number(filter.locationId)
      );
    }

    return filteredItems;
  };

  const handleRefresh = async () => {
    fetchItems();
  };

  const handleAddItem = () => {
    // Navigate to create item page
    import("./createItem").then(() => {
      navigate("/inbound-items/create-item");
    });
  };

  const handleEditItem = (item: InboundItem) => {
    import("./editItem").then(() => {
      navigate(`/inbound-items/${item.id}/edit`, {
        state: { item: item },
      });
    });
  };

  const handleDeleteItem = async () => {
    setIsLoading(true);
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      dispatch(deleteInboundItem({ item: itemToDelete })).unwrap();
      setShowDeleteItemModal(false);
      await fetchItems();
      setItemToDelete(null);
    } catch (error) {
      console.error("Failed to delete item:", error);
    } finally {
      setIsDeleting(false);
      setIsLoading(false);
    }
  };

  const handleToggleDeleteItemModal = (item: InboundItem) => {
    setShowDeleteItemModal(true);
    setItemToDelete(item);
  };

  const handleToggleFilterDrawer = () => {
    setShowFilterDrawer(!showFilterDrawer);
  };

  const handleApplyFilters = (filters: typeof filter) => {
    setFilter(filters);
    updateURLWithFilters(filters);
    setShowFilterDrawer(false);
  };

  const handleClearFilters = (filters: typeof filter) => {
    setFilter(filters);
    updateURLWithFilters(filters);
  };

  const calcActiveFilters = () => {
    let activeFilters = 0;
    if (filter.status !== "all") {
      activeFilters++;
    }
    if (filter.createdBy !== myUserId) {
      activeFilters++;
    }
    if (filter.locationId !== "") {
      activeFilters++;
    }
    if (filter.consignorId !== "") {
      activeFilters++;
    }
    return activeFilters;
  };

  const pageTitle = (
    <div className="flex items-center justify-center gap-2">
      Inbound Items{" "}
      <div className="text-sm flex items-center justify-center">
        ({filteredItems?.length})
      </div>
    </div>
  );

  return (
    <PullToRefresh
      onRefresh={handleRefresh}
      refreshingContent={
        <div className="flex justify-center items-center mt-4">
          <div className="w-8 h-8 border-4 border-t-transparent border-blue-500 rounded-full animate-spin"></div>
        </div>
      }
    >
      <div className="min-h-screen flex flex-col bg-gray-100">
        <Layout title={pageTitle} isDarkMode={true}>
          <div className="bg-primary">
            <div className="p-4 py-6 flex flex-col flex-grow rounded-t-[2rem] bg-gray-100">
              <div className="flex items-center gap-2 p-2">
                {/* Search Input Container */}
                <div className="relative flex-1">
                  <FiSearch className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-3 shadow-bar-shadow rounded-lg"
                    placeholder="Search by Item #, Description, or Consignor Code"
                  />
                </div>

                {/* Filter Button */}
                <button
                  onClick={handleToggleFilterDrawer}
                  className={`flex-shrink-0 border border-gray-200 rounded-lg px-3 py-3 hover:scale-105 transition-all duration-300 flex items-center justify-center gap-1 ${
                    calcActiveFilters() > 0
                      ? "bg-primary text-white"
                      : "bg-white"
                  }`}
                >
                  <FaFilter className="h-3 w-4" />
                  <span className="text-sm font-bold">Filter</span>
                </button>
              </div>

              <div>
                <div className="flex-grow space-y-2 h-[calc(100dvh-230px)] overflow-y-auto hide-scrollbar px-2 pb-8">
                  {/* Subtle refresh indicator at top */}
                  {isUpdating && filteredItems.length > 0 && !isLoading && (
                    <div className="flex items-center justify-center gap-2 py-2 mb-2">
                      <FaSpinner className="animate-spin" />
                      <span className="text-xs text-gray-500 font-medium">
                        Updating
                      </span>
                    </div>
                  )}

                  {/* Loading state */}
                  {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-16">
                      <div className="w-12 h-12 border-4 border-t-transparent border-primary rounded-full animate-spin mb-4"></div>
                      <p className="text-gray-600">Loading items...</p>
                    </div>
                  ) : (
                    <>
                      {/* Items list */}
                      {filteredItems?.map((item) => (
                        <ItemCard
                          key={item.tempId || item.id}
                          item={item}
                          onEdit={handleEditItem}
                          onDelete={handleToggleDeleteItemModal}
                        />
                      ))}

                      {/* Empty state */}
                      {filteredItems?.length === 0 && (
                        <div className="text-center py-8">
                          {searchTerm ? (
                            <p className="text-gray-500">
                              No items found matching "{searchTerm}"
                            </p>
                          ) : (
                            <p className="text-gray-500">No items available</p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="fixed bottom-0 w-full shadow-xl gap-4 p-2">
            <div className="flex items-center justify-center gap-4 mb-2">
              <button
                onClick={handleAddItem}
                className="bg-primary text-white rounded-full p-5 shadow-xl flex justify-center items-center hover:scale-105 transition-all duration-300"
              >
                <FaPlus size={24} />
              </button>
            </div>
          </div>
        </Layout>
        <ConfirmModal
          isOpen={showDeleteItemModal}
          title="Delete Item"
          message={
            <span>
              Are you sure you want to permanently delete{" "}
              <span className="font-bold mr-1">
                {itemToDelete?.description}
              </span>
              ?
            </span>
          }
          confirmText="Delete Item"
          cancelText="Cancel"
          onConfirm={handleDeleteItem}
          onCancel={() => {
            setShowDeleteItemModal(false);
          }}
          showIcon={true}
          isLoading={isDeleting}
          loadingText="Deleting..."
        />
        <FilterDrawer
          isOpen={showFilterDrawer}
          onClose={() => setShowFilterDrawer(false)}
          currentFilters={filter}
          handleClearFilters={handleClearFilters}
          handleApplyFilters={handleApplyFilters}
          packages={packages}
          consignors={consignors}
          locations={locations}
        />
      </div>
    </PullToRefresh>
  );
};

export default InboundItems;
